import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { bridgeApi } from '@/lib/bridgeApi';

interface HomeCareProvider {
  id: string;
  provider_name: string;
  provider_type: string;
  email: string;
  mobile: string;
  alt_contact: string;
  whatsapp?: string;
  city: string;
  state: string;
  address: string;
  pincode: string;
  city_id?: string;
  state_id?: string;
  area_id?: string;
  area?: string;
  // Was missing entirely — HomeCareProfilePage.tsx had to read this via an
  // `as any` cast as a workaround, and since it's not a real property of
  // this type, login()/refreshProvider() below never included it when
  // building their own HomeCareProvider objects either (TypeScript would
  // have flagged excess/unused data, so they just never wrote it) — bio
  // disappeared from the dashboard's cached session the moment either of
  // those ran, even though the backend was saving it correctly all along.
  bio?: string;
  // Same "missing from this type -> silently dropped when login()/
  // refreshProvider() build their own object" gap bio used to have above —
  // these 4 are real `providers` columns the public details page already
  // reads, but had no dashboard edit path until now.
  qualification?: string;
  specialization?: string;
  registration_no?: string;
  languages?: string;
  experience_years: number;
  hourly_rate: number;
  daily_rate: number;
  monthly_rate: number;
  services_offered: string[];
  availability_status: string;
  kyc_status: string;
  is_verified: boolean;
  is_active: boolean;
  subscription_plan: string;
  photo_url: string;
  aadhaar_verified: boolean;
  pan_verified: boolean;
  police_verified: boolean;
  adminSession?: boolean;
  booking_enabled?: boolean;
  booking_auto_disabled?: boolean;
}

interface HomeCareAuthContextType {
  provider: HomeCareProvider | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdminSession: boolean;
  providerToken: string | null;
  login: (email: string, password: string) => Promise<{ success: boolean; message: string }>;
  register: (data: any) => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  refreshProvider: () => Promise<void>;
}

const HomeCareAuthContext = createContext<HomeCareAuthContextType | undefined>(undefined);

const STORAGE_KEY = 'homecare_auth';
const TOKEN_KEY = 'homecare_auth_token';

export const HomeCareAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [provider, setProvider] = useState<HomeCareProvider | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdminSession, setIsAdminSession] = useState(false);
  const [providerToken, setProviderToken] = useState<string | null>(null);

  // Check if this is an admin impersonation session
  const checkAdminImpersonation = (providerId: string): boolean => {
    try {
      // Check for global admin session flag
      const adminSession = localStorage.getItem('adminSession');
      if (adminSession === 'true') return true;
      
      const raw = localStorage.getItem('patakare_impersonation');
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      
      // Accept if admin session with matching member type (4 = HomeCare+) or entity type
      return (
        parsed?.adminSession === true &&
        (parsed?.memberType === 4 || parsed?.entityType === 'homecare_provider')
      );
    } catch {
      return false;
    }
  };

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const isAdminImpersonation = parsed.adminSession || checkAdminImpersonation(parsed.id);
        
        // For admin sessions, skip verification entirely
        if (isAdminImpersonation) {
          setProvider(parsed);
          setIsAdminSession(true);
          setIsLoading(false);
          return;
        }
        
        // For regular sessions, verify but with graceful fallback
        verifyProvider(parsed.id).then((result) => {
          if (result.shouldAccept) {
            setProvider(parsed);
            const storedToken = localStorage.getItem(TOKEN_KEY);
            if (storedToken) setProviderToken(storedToken);
          } else {
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem(TOKEN_KEY);
          }
          setIsLoading(false);
        });
      } catch (e) {
        localStorage.removeItem(STORAGE_KEY);
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, []);

  // Graceful verification - returns shouldAccept: true if API fails or doesn't exist
  const verifyProvider = async (id: string): Promise<{ shouldAccept: boolean; isActive?: boolean }> => {
    try {
      const result = await bridgeApi.call<any>('homecare_provider_verify', { id });
      // API responded successfully
      if (result?.is_active === true) {
        return { shouldAccept: true, isActive: true };
      }
      if (result?.is_active === false) {
        return { shouldAccept: false, isActive: false };
      }
      // Unknown response - accept session
      return { shouldAccept: true };
    } catch (error: any) {
      // API failed or doesn't exist - gracefully accept session
      console.warn('[HomeCareAuth] Verification skipped (API unavailable):', error.message);
      return { shouldAccept: true };
    }
  };

  const login = async (email: string, password: string): Promise<{ success: boolean; message: string }> => {
    setIsLoading(true);
    
    try {
      const result = await bridgeApi.post<any>('homecare_login', { email, password });
      
      if (!result.success) {
        setIsLoading(false);
        return { success: false, message: result.message || 'Login failed' };
      }

      const prov = result.provider;

      // HomeCare+ allows access even in pending status (with notification)
      // Only reject if KYC is explicitly rejected or account is inactive
      if (prov.kyc_status === 'rejected') {
        setIsLoading(false);
        return { success: false, message: 'Your KYC was rejected. Please contact support.' };
      }

      if (prov.is_active === false || prov.is_active === 0) {
        setIsLoading(false);
        return { success: false, message: 'Your account is currently inactive. Please contact support.' };
      }

      const services = Array.isArray(prov.services_offered) 
        ? prov.services_offered 
        : (typeof prov.services_offered === 'string' ? (JSON.parse(prov.services_offered) || []) : []);

      const providerData: HomeCareProvider = {
        id: prov.id,
        provider_name: prov.provider_name || prov.full_name || '',
        provider_type: prov.provider_type || '',
        email: prov.email || '',
        mobile: prov.mobile || prov.contact_number || '',
        alt_contact: prov.alt_contact || '',
        whatsapp: prov.whatsapp || '',
        city: prov.city || '',
        state: prov.state || '',
        // city_id/state_id/area_id/area were missing here even though the
        // backend's login response already includes them (api.php) — this
        // object is built field-by-field instead of spreading the API
        // response, so these were silently dropped the moment they hit the
        // frontend, before ever reaching localStorage/LocationSelector.
        city_id: prov.city_id != null ? String(prov.city_id) : undefined,
        state_id: prov.state_id != null ? String(prov.state_id) : undefined,
        area_id: prov.area_id != null ? String(prov.area_id) : undefined,
        area: prov.area || '',
        address: prov.address || '',
        pincode: prov.pincode || '',
        experience_years: Number(prov.experience_years) || 0,
        hourly_rate: Number(prov.hourly_rate) || 0,
        daily_rate: Number(prov.daily_rate) || 0,
        monthly_rate: Number(prov.monthly_rate) || 0,
        services_offered: services,
        availability_status: prov.availability_status || 'available',
        bio: prov.bio || '',
        qualification: prov.qualification || '',
        specialization: prov.specialization || '',
        registration_no: prov.registration_no || '',
        languages: prov.languages || '',
        kyc_status: prov.kyc_status || 'pending',
        is_verified: Boolean(prov.is_verified),
        is_active: Boolean(prov.is_active),
        subscription_plan: prov.subscription_plan || 'free',
        photo_url: prov.photo_url || '',
        aadhaar_verified: Boolean(prov.aadhaar_verified),
        pan_verified: Boolean(prov.pan_verified),
        police_verified: Boolean(prov.police_verified),
        booking_enabled: Boolean(prov.booking_enabled),
        booking_auto_disabled: Boolean(prov.booking_auto_disabled),
      };

      setProvider(providerData);
      setIsAdminSession(false);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(providerData));

      if (result.token) {
        setProviderToken(result.token);
        localStorage.setItem(TOKEN_KEY, result.token);
      }

      // Same install-linking MemberAuthContext's login() already does — was
      // missing here entirely, so a HomeCare provider who installed the PWA
      // then logged in stayed permanently attributed to "Guest" in Install
      // Analytics, even though they're a real, identifiable account.
      const numericId = typeof providerData.id === 'string' ? parseInt(providerData.id) : Number(providerData.id);
      if (numericId > 0) {
        import('@/utils/installTracker').then(({ linkInstallToUser, isAppInstalled }) => {
          if (isAppInstalled()) linkInstallToUser(numericId).catch(() => {});
        });
      }

      setIsLoading(false);

      return { success: true, message: 'Login successful!' };
    } catch (err: any) {
      setIsLoading(false);
      return { success: false, message: err.message || 'An error occurred. Please try again.' };
    }
  };

  const register = async (data: any): Promise<{ success: boolean; message: string }> => {
    try {
      const result = await bridgeApi.post<any>('homecare_register', data);
      if (result && result.success === false) {
        return { success: false, message: result.error || result.errors?.join(', ') || 'Registration failed' };
      }
      return { success: true, message: result?.message || 'Registration submitted' };
    } catch (err: any) {
      return { success: false, message: err.message || 'Registration failed' };
    }
  };

  const logout = () => {
    setProvider(null);
    setIsAdminSession(false);
    setProviderToken(null);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('patakare_impersonation');
  };

  const refreshProvider = async () => {
    if (!provider) return;

    try {
      const result = await bridgeApi.call<any>('homecare_provider_get', { id: provider.id, token: providerToken });
      // Handle both {data: {...}} and direct object
      const data = result?.data || result;
      let activePlan = data?.subscription_plan || 'free';

      try {
        const subResult = await bridgeApi.call<any>('homecare_subscription_get', { provider_id: provider.id });
        const subData = subResult?.data || subResult;
        if (subData?.status === 'active' && subData?.plan_name) {
          activePlan = subData.plan_name;
        }
      } catch {}

      if (data && (data.provider_name || data.business_name)) {
        const services = Array.isArray(data.services_offered) 
          ? data.services_offered 
          : (typeof data.services_offered === 'string' ? (JSON.parse(data.services_offered) || []) : []);
          
        const updated: HomeCareProvider = {
          id: data.id,
          provider_name: data.provider_name || data.business_name || data.full_name || '',
          provider_type: data.provider_type || '',
          email: data.email || '',
          mobile: data.mobile || data.contact_number || '',
          alt_contact: data.alt_contact || '',
          whatsapp: data.whatsapp || '',
          city: data.city || '',
          state: data.state || '',
          // Same gap as login()'s providerData just above — this function
          // rebuilds its own object instead of spreading `data`, and had
          // been silently dropping these 4 fields on every single refresh
          // (Dashboard mount, after any save, KYC/Settings actions), even
          // after the backend started returning them correctly. Confirmed
          // live: city/state displayed fine post-refresh but Area and the
          // LocationSelector's underlying ids did not.
          city_id: data.city_id != null ? String(data.city_id) : undefined,
          state_id: data.state_id != null ? String(data.state_id) : undefined,
          area_id: data.area_id != null ? String(data.area_id) : undefined,
          area: data.area || '',
          address: data.address || '',
          pincode: data.pincode || '',
          experience_years: Number(data.experience_years) || 0,
          hourly_rate: Number(data.hourly_rate) || 0,
          daily_rate: Number(data.daily_rate) || 0,
          monthly_rate: Number(data.monthly_rate) || 0,
          services_offered: services,
          availability_status: data.availability_status || 'available',
          bio: data.bio || '',
          qualification: data.qualification || '',
          specialization: data.specialization || '',
          registration_no: data.registration_no || '',
          languages: data.languages || '',
          kyc_status: data.kyc_status || 'pending',
          is_verified: Boolean(data.is_verified),
          is_active: Boolean(data.is_active),
          subscription_plan: activePlan,
          photo_url: data.photo_url || '',
          aadhaar_verified: Boolean(data.aadhaar_verified),
          pan_verified: Boolean(data.pan_verified),
          police_verified: Boolean(data.police_verified),
          booking_enabled: Boolean(data.booking_enabled),
          booking_auto_disabled: Boolean(data.booking_auto_disabled),
          adminSession: provider.adminSession,
        };
        setProvider(updated);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      }
    } catch (e) {
      console.error('Failed to refresh provider:', e);
    }
  };

  return (
    <HomeCareAuthContext.Provider
      value={{
        provider,
        isLoading,
        isAuthenticated: !!provider,
        isAdminSession,
        providerToken,
        login,
        register,
        logout,
        refreshProvider,
      }}
    >
      {children}
    </HomeCareAuthContext.Provider>
  );
};

export const useHomeCareAuth = () => {
  const context = useContext(HomeCareAuthContext);
  if (context === undefined) {
    throw new Error('useHomeCareAuth must be used within a HomeCareAuthProvider');
  }
  return context;
};
